import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company: "", role: "recruiter" });
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="size-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><Sparkles className="size-4" /></div>
          <div className="display text-lg font-extrabold">ResumeForge</div>
        </div>
        <h1 className="display text-3xl font-black tracking-tighter">Create your workspace</h1>
        <p className="text-muted-foreground mt-1 text-sm">Start standardizing resumes in minutes.</p>

        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="register-form">
          <div>
            <Label htmlFor="fn" className="text-xs font-semibold">Full name</Label>
            <Input id="fn" required value={form.full_name} onChange={set("full_name")} className="mt-1.5 h-11" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="em" className="text-xs font-semibold">Work email</Label>
            <Input id="em" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="pw" className="text-xs font-semibold">Password</Label>
            <Input id="pw" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 h-11" data-testid="register-password-input" />
          </div>
          <div>
            <Label htmlFor="co" className="text-xs font-semibold">Company</Label>
            <Input id="co" value={form.company} onChange={set("company")} className="mt-1.5 h-11" data-testid="register-company-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="register-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recruiter">Recruiter</SelectItem>
                <SelectItem value="data_entry">Data Entry Operator</SelectItem>
                <SelectItem value="company_admin">Company Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 lift gap-2" data-testid="register-submit-button">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />} Create account
          </Button>
          <div className="text-xs text-muted-foreground">Already have an account? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="link-login">Sign in</Link></div>
        </form>
      </div>
    </div>
  );
}
